import React, { useState } from 'react'
import { Box, colors } from '@mui/material'
import { blue, grey } from '@mui/material/colors'
import CalendarDatePicker from './CalendarDatePicker'

const getBackgroundColor = (isToday, isDifferentMonth, view) => {
  if (view === 'day') return 'white'
  if (isToday) return blue[50]
  if (isDifferentMonth) return grey[200]
  return 'white'
}

const getDaysInMonth = (month, year) => {
  const date = new Date(year, month, 1)

  const days = []

  if (date.getDay() !== 0) {
    date.setDate(date.getDate() - date.getDay())
  }

  while (
    date.getMonth() === month ||
    date.getMonth() === (month - 1 < 0 ? 11 : month - 1)
  ) {
    days.push(new Date(date))
    date.setDate(date.getDate() + 1)
  }

  if (days.length % 7 !== 0) {
    while (days.length % 7 > 0) {
      days.push(new Date(date))
      date.setDate(date.getDate() + 1)
    }
  }

  return days
}

const getDaysInWeek = (focusDate) => {
  return new Array(7)
    .fill(null)
    .map(
      (_, i) =>
        new Date(
          focusDate.setDate(focusDate.getDate() - focusDate.getDay() + i)
        )
    )
}

const getDays = (focusDate, view) => {
  if (view === 'month')
    return getDaysInMonth(focusDate.getMonth(), focusDate.getFullYear())

  if (view === 'week') return getDaysInWeek(focusDate)

  return [focusDate]
}

const Calendar = ({ activities, activityTypes }) => {
  const today = new Date()

  const [focusDate, setFocusDate] = useState(today)

  const [view, setView] = useState('month')

  const days = getDays(new Date(focusDate), view)

  const rows = Math.ceil(days.length / 7)

  const isDayView = view === 'day'

  const daysInMonth = days
    .filter((day) => day.getMonth() === focusDate.getMonth())
    .map((day) => day.toDateString())

  const monthMatchesDict = activities
    .filter((a) => a && daysInMonth.includes(new Date(a.date).toDateString()))
    .reduce((acc, item) => {
      if (acc[item.activityTypeId]) {
        const old = acc[item.activityTypeId]
        acc[item.activityTypeId] = {
          ...old,
          count: item.count + old.count,
          revenue: item.revenue + old.revenue,
        }
      } else {
        acc[item.activityTypeId] = { ...item }
      }
      return acc
    }, {})

  const monthMatches = Object.values(monthMatchesDict)

  const getActivitiesList = (mat, i, isTotal) => {
    const activityType = activityTypes.find(
      (at) => at.id === mat.activityTypeId
    )

    return (
      <Box
        key={`at-${i}`}
        sx={{
          fontSize: isDayView ? '18px' : null,
          fontWeight: 'bold',
          p: isDayView ? 1 : 0.25,
          borderRadius: '2px',
          backgroundColor: colors[activityType?.color]?.[100],
          border: `1px solid ${colors[activityType?.color]?.[200]}`,
          mb: 0.5,
          '&:last-child': {
            mb: 2,
          },
          mt: isDayView ? 1 : null,
        }}
      >
        {activityType?.name}: {mat.activityTypeId === 6 && '$'}
        {mat.count || 0}
      </Box>
    )
  }

  return (
    <>
      <Box>
        <CalendarDatePicker
          view={view}
          setView={setView}
          setFocusDate={setFocusDate}
          focusDate={focusDate}
        />
        <Box
          sx={{
            display: 'flex',
          }}
        >
          {view !== 'day' &&
            ['S', 'M', 'T', 'W', 'T', 'F', 'S', 'Totals'].map((d, i) => (
              <Box
                key={i}
                sx={{
                  backgroundColor: 'white',
                  width: '120px',
                  height: '35px',
                  border: '1px solid',
                  borderLeft: i === 0 ? '1px solid' : 'none',
                  borderBottom: 'none',
                  p: 0.5,
                }}
              >
                {d}
              </Box>
            ))}
        </Box>

        {new Array(rows).fill().map((_, rowI) => {
          const firstIndex = rowI * 7
          const lastIndex = firstIndex + 6

          const daysInRow = days
            .slice(firstIndex, lastIndex + 1)
            .map((day) => day.toDateString())

          const totalsMatchesDict = activities
            .filter(
              (a) => a && daysInRow.includes(new Date(a.date).toDateString())
            )
            .reduce((acc, item) => {
              if (acc[item.activityTypeId]) {
                const old = acc[item.activityTypeId]
                acc[item.activityTypeId] = {
                  ...old,
                  count: item.count + old.count,
                  revenue: item.revenue + old.revenue,
                }
              } else {
                acc[item.activityTypeId] = { ...item }
              }
              return acc
            }, {})

          const totalsMatches = Object.values(totalsMatchesDict)

          return (
            <Box sx={{ display: 'flex' }} key={rowI}>
              {new Array(7).fill().map((_, dayI) => {
                const dayIndex = rowI * 7 + dayI
                const day = days[dayIndex]

                if (!day) return null
                const isToday = day.toDateString() === today.toDateString()

                const matches = activities.filter(
                  (a) =>
                    a && new Date(a.date).toDateString() === day.toDateString()
                )

                const isDifferentMonth = day.getMonth() !== focusDate.getMonth()

                return (
                  <Box
                    onClick={() => {
                      setFocusDate(day)
                      setView('day')
                    }}
                    sx={{
                      width: isDayView ? '700px' : '120px',
                      height: rows === 1 ? '300px' : '80px',
                      borderRight: '1px solid',
                      borderLeft: dayI === 0 ? '1px solid' : null,
                      borderBottom: '1px solid',
                      borderTop: rowI === 0 ? '1px solid' : null,
                      p: isDayView ? 2 : 0.5,
                      overflow: 'hidden',
                      cursor: 'pointer',
                      backgroundColor: getBackgroundColor(
                        isToday,
                        isDifferentMonth,
                        view
                      ),
                    }}
                    key={`${rowI}-${dayI}`}
                  >
                    <Box
                      sx={{
                        width: isDayView ? '40px' : '28px',
                        height: isDayView ? '40px' : '28px',

                        borderRadius: '50%',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        backgroundColor: isToday ? blue[100] : null,
                        fontSize: isDayView ? '24px' : null,
                      }}
                    >
                      {day.getDate()}
                    </Box>

                    {matches.length > 0 && (
                      <Box
                        sx={{
                          fontSize: '10px',
                          mt: 0.5,
                          height: 'calc(100% - 32px)',
                          overflow: 'scroll',
                        }}
                      >
                        {matches.map(getActivitiesList)}
                      </Box>
                    )}
                  </Box>
                )
              })}

              {!isDayView && (
                <Box
                  onClick={() => {}}
                  sx={{
                    width: '120px',
                    height: rows === 1 ? '300px' : '80px',
                    borderRight: '1px solid',

                    borderBottom: '1px solid',
                    borderTop: rowI === 0 ? '1px solid' : null,
                    p: 0.5,
                    overflow: 'hidden',

                    backgroundColor: getBackgroundColor(false, false, view),
                  }}
                >
                  {totalsMatches.length > 0 && (
                    <Box
                      sx={{
                        fontSize: '10px',
                        mt: 0.5,
                        height: '100%',
                        overflow: 'scroll',
                      }}
                    >
                      {totalsMatches.map(getActivitiesList)}
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          )
        })}
        {view === 'month' && (
          <Box sx={{ display: 'flex' }}>
            {new Array(6).fill().map(() => {
              return (
                <Box
                  sx={{
                    width: '142.5px',
                    height: '80px',
                    p: 0.5,
                  }}
                />
              )
            })}
            <Box
              sx={{
                width: '120px',
                height: '80px',
                backgroundColor: 'white',
                p: 0.5,
                overflow: 'hidden',
                border: '1px solid',
                borderTop: 'none',
              }}
            >
              {monthMatches.length > 0 && (
                <Box
                  sx={{
                    fontSize: '10px',
                    mt: 0.5,
                    height: '100%',
                    overflow: 'scroll',
                  }}
                >
                  {monthMatches.map(getActivitiesList)}
                </Box>
              )}
            </Box>
          </Box>
        )}
      </Box>
    </>
  )
}

export default Calendar
